import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  TableRowActionsMenu,
  ConfirmDialog,
  Typography,
  useConfirmDialog,
  snackbar,
} from '@occc-app/design-system';
import { useProductGroupDelete } from '@occc-app/dataAccessLayer/productGroup/mutations';
import { routeCreator } from '@occc-app/routes/ProductGroup/utils';

type ProductGroupActionsContainerProps = {
  id: string;
  name: string;
  isUserPermittedToEditProductGroup: boolean;
  isUserPermittedToDeleteProductGroup: boolean;
};

export const ProductGroupActionsContainer = ({
  id,
  name,
  isUserPermittedToEditProductGroup,
  isUserPermittedToDeleteProductGroup,
}: ProductGroupActionsContainerProps) => {
  const navigate = useNavigate();

  const onDeleteCompleted = () => snackbar.success('Product Group has been successfully deleted.');

  const [productGroupDelete] = useProductGroupDelete({
    onCompleted: onDeleteCompleted,
    refetchQueries: ['productGroups'],
  });

  const onDelete = () => productGroupDelete({ variables: { id } });

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onDelete,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (isUserPermittedToEditProductGroup) {
      items.push({
        label: 'Edit Product Group',
        onClick: () => navigate(routeCreator.update(id)),
        dataTestId: 'edit',
      });
    }

    if (isUserPermittedToDeleteProductGroup) {
      items.push({
        label: 'Delete Product Group',
        onClick: () => setOpen(true),
        dataTestId: 'delete',
      });
    }

    return items;
  }, [
    id,
    navigate,
    setOpen,
    isUserPermittedToEditProductGroup,
    isUserPermittedToDeleteProductGroup,
  ]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />
      <ConfirmDialog
        dialogTitle='Are you sure?'
        open={open}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onConfirmButtonClick,
          color: 'error',
        }}
      >
        <Typography>This action will delete the Product Group "{name}".</Typography>
      </ConfirmDialog>
    </>
  );
};
